import { Component, type ErrorInfo, type ReactNode } from "react";
import { Icon } from "@iconify/react";

interface Props {
  children: ReactNode;
  /** Минимальная высота fallback-блока (по умолчанию 96px) */
  minHeight?: string;
  /** Кастомный fallback — если не передан, показывается встроенный */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

/**
 * Оборачивает отдельный UI-блок. Если блок крашнется (runtime error, неожиданные данные
 * от API и т.д.) — показывается тихий fallback вместо того, чтобы упал весь сайт.
 *
 * @example
 * <BlockErrorBoundary>
 *   <KpiSection />
 * </BlockErrorBoundary>
 */
export class BlockErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: "" };
  }

  static getDerivedStateFromError(error: unknown): State {
    const msg = error instanceof Error ? error.message : String(error);
    return { hasError: true, errorMessage: msg };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // В production можно подключить Sentry здесь
    console.error("[BlockErrorBoundary] Caught error:", error, info.componentStack);
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          className="flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/[0.03] text-neutral-500"
          style={{ minHeight: this.props.minHeight ?? "96px" }}
        >
          <Icon icon="solar:danger-triangle-outline" className="h-4 w-4 shrink-0" />
          <span className="text-sm">Не удалось загрузить блок</span>
        </div>
      );
    }

    return this.props.children;
  }
}
